import useApi from '../../../hooks/useApi';
import { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function UserDashboard() {
  const { data: userMetrics, loading, error } = useApi('/analytics/user');
  const [monthlyData, setMonthlyData] = useState([]);

  // Simulate monthly data for user's waste requests
  useEffect(() => {
    if (userMetrics?.wasteRequests) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const data = months.map(() => Math.floor(Math.random() * (userMetrics.wasteRequests / 2)));
      setMonthlyData(data);
    }
  }, [userMetrics]);

  // Chart.js data for the histogram
  const chartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Your Waste Requests',
        data: monthlyData,
        backgroundColor: '#10B981', // Green color for user dashboard
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: '#E5E7EB' } },
    },
    plugins: { legend: { display: false } },
  };

  // Calculate progress for the gauge (e.g., based on user's waste processed)
  const targetWasteProcessed = 100; // User-specific target (in kg)
  const progress = userMetrics?.wasteProcessed
    ? Math.min((userMetrics.wasteProcessed / targetWasteProcessed) * 100, 100)
    : 0;

  return (
    <div className="p-5 dark:bg-gray-900 min-h-screen">
      {loading ? (
        <div className="text-center dark:text-white">Loading...</div>
      ) : error ? (
        <div className="text-red-500 text-center">{error}</div>
      ) : (
        <>
          {/* Cards Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
            {[
              { label: 'Your Waste Requests', value: userMetrics?.wasteRequests || 0, change: 8.5, positive: true, icon: '🗑️' },
              { label: 'Waste Processed (kg)', value: userMetrics?.wasteProcessed || 0, change: 6.3, positive: true, icon: '♻️' },
            ].map((metric) => (
              <div
                key={metric.label}
                className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md flex items-center space-x-4"
              >
                <div className="text-3xl">{metric.icon}</div>
                <div>
                  <h3 className="text-lg font-semibold dark:text-white">{metric.label}</h3>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{metric.value}</p>
                  <p
                    className={`text-sm ${
                      metric.positive ? 'text-green-500' : 'text-red-500'
                    }`}
                  >
                    {metric.positive ? '↑' : '↓'} {metric.change}%
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 gap-5">
            {/* Monthly Waste Requests Histogram */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4 dark:text-white">Your Monthly Waste Requests</h3>
              <div className="h-64">
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>

            {/* Waste Processed Gauge */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4 dark:text-white">Your Waste Processed Target</h3>
              <div className="flex flex-col items-center">
                <div className="w-40 h-40 mb-4">
                  <CircularProgressbar
                    value={progress}
                    text={`${progress.toFixed(2)}%`}
                    styles={buildStyles({
                      pathColor: '#10B981',
                      textColor: '#10B981',
                      trailColor: '#E5E7EB',
                    })}
                  />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
                  You've processed {userMetrics?.wasteProcessed || 0} kg this month, great job!
                </p>
                <div className="flex justify-between w-full mt-4">
                  <div>
                    <p className="text-sm dark:text-white">Target</p>
                    <p className="text-lg font-bold text-red-500">{targetWasteProcessed} kg ↓</p>
                  </div>
                  <div>
                    <p className="text-sm dark:text-white">Processed</p>
                    <p className="text-lg font-bold text-green-500">
                      {userMetrics?.wasteProcessed || 0} kg ↑
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default UserDashboard;